import { BaseEntity } from "../core/entity/BaseEntity";
import Entity from "../core/entity/Entity";
import { on } from "../core/entity/handler";
import type { Body } from "../core/physics/body/Body";

const MAX_POSITION = 10000;
const MAX_VELOCITY = 1000;

/**
 * Validates physics state after each physics step and resets any bodies
 * that have gone unstable (NaN/Infinity values or extreme positions/velocities).
 *
 * This is a safety net for debugging unstable physics - it shouldn't be needed
 * in a stable simulation but prevents bodies from flying off to infinity.
 */
export class PhysicsValidator extends BaseEntity {
  @on("afterPhysicsStep")
  onAfterPhysicsStep() {
    const warnings: Array<{
      owner: string;
      issue: "reset" | "clamp";
      details: {
        position?: [number, number];
        velocity: [number, number];
        angularVelocity: number;
        speed?: number;
        maxVelocity?: number;
      };
    }> = [];

    for (const body of this.game.world.bodies) {
      if (body.motion === "static") continue;

      const [x, y] = body.position;
      const [vx, vy] = body.velocity;
      const ownerName =
        (body as Body & { owner?: Entity }).owner?.constructor?.name ??
        "unknown";

      const positionBad =
        !isFinite(x) ||
        !isFinite(y) ||
        Math.abs(x) > MAX_POSITION ||
        Math.abs(y) > MAX_POSITION;
      const velocityBad = !isFinite(vx) || !isFinite(vy);

      if (positionBad || velocityBad) {
        warnings.push({
          owner: ownerName,
          issue: "reset",
          details: {
            position: [x, y],
            velocity: [vx, vy],
            angularVelocity: body.angularVelocity,
          },
        });

        body.position.set(0, 0);
        body.velocity.set(0, 0);
        body.angularVelocity = 0;
        continue;
      }

      const speed = Math.hypot(vx, vy);
      if (speed > MAX_VELOCITY) {
        warnings.push({
          owner: ownerName,
          issue: "clamp",
          details: {
            velocity: [vx, vy],
            angularVelocity: body.angularVelocity,
            speed,
            maxVelocity: MAX_VELOCITY,
          },
        });

        const scale = MAX_VELOCITY / speed;
        body.velocity[0] *= scale;
        body.velocity[1] *= scale;
      }
    }

    if (warnings.length > 0) {
      console.warn(
        "Physics validator detected instability in bodies:",
        warnings,
      );
    }
  }
}
